import { useEffect, useRef } from 'react'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import * as Popover from '@radix-ui/react-popover'

import type { Notification } from '@jotta/types/Notifications'
import { Box } from '@jotta/ui/Box'
import { BrandIcon } from '@jotta/ui/BrandIcon'
import SvgX from '@jotta/ui/SvgX'

import { getBrandZIndex } from '../themes/zIndex'

import { NotificationsListItem } from './NotificationsListItem'

interface NotificationsListProps {
  notifications: Notification[]
  hasNextPage: boolean
  onFetchNextPage: () => void
  isFetchingNextPage: boolean
}

export const NotificationsList: React.FC<NotificationsListProps> = ({
  notifications = [],
  onFetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const notificationsTargetRef = useRef<HTMLAnchorElement>(null)

  useEffect(() => {
    const element = notificationsTargetRef.current

    if (!element || !hasNextPage || isFetchingNextPage) {
      return
    }

    const observer = new IntersectionObserver(
      ([target]) => {
        if (target.isIntersecting && hasNextPage) {
          onFetchNextPage()
        }
      },
      {
        threshold: 0.01,
      },
    )
    observer.observe(element)

    return () => {
      observer.unobserve(element)
      observer.disconnect()
    }
  }, [hasNextPage, onFetchNextPage, isFetchingNextPage])

  return (
    <Box bg="background">
      <Box
        as="header"
        sx={{
          bg: 'background',
          borderBottom: '1px solid',
          borderBottomColor: 'lightGrey',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          position: 'sticky',
          top: '-1px',
          width: 'auto',
        }}
        style={{ zIndex: getBrandZIndex('dropdown') }}
      >
        {/* focusable heading for screen readers */}{' '}
        <span id="notifications-heading" tabIndex={-1}>
          <Trans>Notifications</Trans>
        </span>
        <Popover.Close aria-label={t`Close`} type="button">
          <BrandIcon
            icon={SvgX}
            width={24}
            height={24}
            aria-hidden="true"
            focusable="false"
          />
        </Popover.Close>
      </Box>

      {!notifications.length && (
        <Box p="3">
          <Trans>You don't have any notifications</Trans>
        </Box>
      )}

      <Box
        sx={{
          maxHeight: 'min(var(--content-height), 700px)',
          overflowY: 'auto',
        }}
      >
        {notifications.map(notification => (
          <NotificationsListItem
            ref={
              notifications[notifications.length - 1].id === notification.id
                ? notificationsTargetRef
                : null
            }
            key={notification.id}
            notification={notification}
          />
        ))}
      </Box>
    </Box>
  )
}
